import { CreditCard } from 'data/shared/__autogenerated__/types/CreditCard';
import { EarnRateInfo } from 'data/types/CardRewardsEarnRates';
import { CategoryType } from 'data/shared/__autogenerated__/types/CreditCardCategoryTypes';
import { useScreenSize } from '../hooks/useScreenSize';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faShareNodes,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';
import { faReddit } from '@fortawesome/free-brands-svg-icons';
import { formatSpendCategory } from '../data/shared/utils/formatSpendCategory';
import { formatEarnRate } from '../data/shared/utils/formatEarnRate';
import { categoryEmojiMap } from '../data/shared/utils/CategoryEmojiMap.web';
import { Modal } from './design-system/Modal';
import { ActionButton } from './design-system/ActionButton';
import { SegmentedControl } from './design-system/SegmentedControl';
import { Tooltip } from './Tooltip';
import { saveWallet } from '../services/walletShareApi';
import { getTopEarnRatesForCards } from '../utils/getTopEarnRatesForCards';
import { generateRedditMarkdown } from '../utils/generateRedditMarkdown';
import { shouldShowCategory } from 'data/shared/utils/categoryFilterUtils';
import { copyToClipboard } from '../utils/cardFilePath';

// Get emoji string for a category (for use in share text)
const getCategoryEmojiString = (category: string): string => {
  const emoji = categoryEmojiMap[category];

  // If we got a React.ReactNode (image), we need to fallback
  // For text contexts, we'll use a default emoji
  if (typeof emoji === 'string') {
    return emoji;
  }

  // If it's an image (React.ReactNode), try parent categories for string emoji
  if (emoji) {
    const segments = category.split('.');
    while (segments.length > 1) {
      segments.pop();
      const parentCategory = segments.join('.');
      const parentEmoji = categoryEmojiMap[parentCategory];
      if (typeof parentEmoji === 'string') {
        return parentEmoji;
      }
    }
  }

  return '💳';
};

export type CategoryEarnRatesData = Partial<
  Record<
    CategoryType,
    {
      cardsWithCredits: EarnRateInfo[];
      sortedCardsByEarnRate: EarnRateInfo[];
    }
  >
>;

export const WalletActionButtons = ({
  selectedCards,
  onRemoveAll,
  variant = 'default',
  savedUrl,
  setSavedUrl,
  children,
  categoryEarnRates,
  defaultCategoryEarnRates,
}: {
  selectedCards: CreditCard[];
  onRemoveAll?: () => void;
  variant?: 'default' | 'desktop';
  savedUrl: string | null;
  setSavedUrl: (url: string | null) => void;
  children?: (parts: {
    linkArea: React.ReactNode;
    shareButton: React.ReactNode;
    redditButton: React.ReactNode;
  }) => React.ReactNode;
  categoryEarnRates?: CategoryEarnRatesData;
  defaultCategoryEarnRates?: CategoryEarnRatesData;
}) => {
  const screenSize = useScreenSize();
  const isDesktop = screenSize === 'desktop';
  const [copied, setCopied] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [redditCopied, setRedditCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [showRemoveAllModal, setShowRemoveAllModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTab, setShareTab] = useState<'text' | 'reddit' | 'url'>('text');
  const [shareMessage, setShareMessage] = useState('');
  const [redditMarkdown, setRedditMarkdown] = useState('');
  const [isSavingLink, setIsSavingLink] = useState(false);
  const [includeSettings, setIncludeSettings] = useState(true);
  const [baseShareUrl, setBaseShareUrl] = useState('');
  const [textFormat, setTextFormat] = useState<'compact' | 'full'>('compact');

  // Get top earn rates for share message (compact mode)
  // Always includes allPurchases + top 4 bonus categories = 5 total
  const getFormattedEarnRates = (rates?: CategoryEarnRatesData) => {
    const topRates = getTopEarnRatesForCards(selectedCards, { limit: 5 });

    const results = topRates.map(({ category, earnRate, rewardsType }) => ({
      category,
      rate: earnRate,
      emoji: getCategoryEmojiString(category),
      formattedRate: formatEarnRate(earnRate, rewardsType),
      formattedCategory: formatSpendCategory(category),
    }));

    // Check if allPurchases is already included
    const hasAllPurchases = results.some(
      (r) => r.category === 'allPurchases' || r.category === 'all_purchases'
    );

    // If not included, add it from categoryEarnRates
    if (!hasAllPurchases && rates) {
      const allPurchasesData =
        rates['allPurchases' as keyof typeof rates] ||
        rates['all_purchases' as keyof typeof rates];
      if (allPurchasesData?.sortedCardsByEarnRate?.length) {
        const bestCard = allPurchasesData.sortedCardsByEarnRate[0];
        results.push({
          category: 'allPurchases',
          rate: bestCard.earnRate,
          emoji: getCategoryEmojiString('allPurchases'),
          formattedRate: formatEarnRate(bestCard.earnRate, bestCard.rewardsType),
          formattedCategory: formatSpendCategory('allPurchases'),
        });
      }
    }

    // Sort: allPurchases last, others by rate descending
    return results.sort((a, b) => {
      const aIsAll = a.category === 'allPurchases' || a.category === 'all_purchases';
      const bIsAll = b.category === 'allPurchases' || b.category === 'all_purchases';
      if (aIsAll && !bIsAll) {
        return 1;
      }
      if (!aIsAll && bIsAll) {
        return -1;
      }
      return b.rate - a.rate;
    });
  };

  // Get all earn rates from categoryEarnRates (full mode)
  // Uses same filtering logic as the UI table to avoid redundant subcategories
  const getFullFormattedEarnRates = (rates: CategoryEarnRatesData) => {
    const results: Array<{
      category: string;
      rate: number;
      emoji: string;
      formattedRate: string;
      formattedCategory: string;
    }> = [];

    Object.entries(rates).forEach(([category, data]) => {
      if (!data?.sortedCardsByEarnRate?.length) {
        return;
      }
      const bestCard = data.sortedCardsByEarnRate[0];
      if (!bestCard) {
        return;
      }

      // Apply same filtering as the UI table
      if (!shouldShowCategory(category, rates)) {
        return;
      }

      results.push({
        category,
        rate: bestCard.earnRate,
        emoji: getCategoryEmojiString(category),
        formattedRate: formatEarnRate(bestCard.earnRate, bestCard.rewardsType),
        formattedCategory: formatSpendCategory(category),
      });
    });

    // Sort by earn rate descending
    return results.sort((a, b) => b.rate - a.rate);
  };

  const generateShareMessage = (shareUrl: string, format: 'compact' | 'full'): string => {
    const ratesData = includeSettings
      ? categoryEarnRates
      : defaultCategoryEarnRates || categoryEarnRates;

    let message = 'This wallet currently earns:\n';

    if (format === 'compact') {
      const topEarnRates = getFormattedEarnRates(ratesData);
      topEarnRates.forEach(({ emoji, formattedRate, formattedCategory }) => {
        message += `${emoji} ${formattedRate} ${formattedCategory}\n`;
      });
      message += `…and more -- ${shareUrl}`;
    } else {
      // Full mode - use categoryEarnRates
      const ratesData = includeSettings
        ? categoryEarnRates
        : defaultCategoryEarnRates || categoryEarnRates;
      if (ratesData) {
        const allRates = getFullFormattedEarnRates(ratesData);
        allRates.forEach(({ emoji, formattedRate, formattedCategory }) => {
          message += `${emoji} ${formattedRate} ${formattedCategory}\n`;
        });
      }
      message += `\n${shareUrl}`;
    }

    return message;
  };

  const buildSavedWalletLink = async (): Promise<string> => {
    if (savedUrl) {
      return savedUrl;
    }
    const code = await saveWallet(selectedCards);
    const url = `${window.location.origin}/w/${code}`;
    setSavedUrl(url);
    return url;
  };

  const getShareUrl = async (): Promise<string> => {
    try {
      return await buildSavedWalletLink();
    } catch {
      const fallback = window.location.href;
      setSavedUrl(fallback);
      return fallback;
    }
  };

  // Get current settings query params (excluding card-related params like 'w')
  const getSettingsQueryParams = (): string => {
    const currentParams = new URLSearchParams(window.location.search);
    const settingsParams = new URLSearchParams();

    // Copy over settings params (not card-related ones)
    currentParams.forEach((value, key) => {
      if (key !== 'w') {
        settingsParams.set(key, value);
      }
    });

    const queryString = settingsParams.toString();
    return queryString ? `?${queryString}` : '';
  };

  const getShareUrlWithSettings = (
    baseUrl: string,
    withSettings: boolean
  ): string => {
    if (!withSettings) {
      return baseUrl;
    }
    const settingsQuery = getSettingsQueryParams();
    return baseUrl + settingsQuery;
  };

  const handleOpenShareModal = async (tab: 'text' | 'reddit' | 'url') => {
    setShareTab(tab);
    setIsSavingLink(true);
    const shareUrl = await getShareUrl();
    setBaseShareUrl(shareUrl);

    const finalUrl = getShareUrlWithSettings(shareUrl, includeSettings);

    // Generate text share message
    setShareMessage(generateShareMessage(finalUrl, textFormat));

    // Generate reddit markdown
    const ratesForMarkdown = includeSettings
      ? categoryEarnRates
      : defaultCategoryEarnRates || categoryEarnRates;
    if (ratesForMarkdown && Object.keys(ratesForMarkdown).length > 0) {
      const markdown = generateRedditMarkdown(ratesForMarkdown, finalUrl, selectedCards);
      setRedditMarkdown(markdown);
    }

    setShowShareModal(true);
    setIsSavingLink(false);
  };

  const handleTextFormatChange = (format: 'compact' | 'full') => {
    setTextFormat(format);
    if (!baseShareUrl) {
      return;
    }

    const finalUrl = getShareUrlWithSettings(baseShareUrl, includeSettings);
    setShareMessage(generateShareMessage(finalUrl, format));
  };

  const handleIncludeSettingsChange = (checked: boolean) => {
    setIncludeSettings(checked);
    if (!baseShareUrl) {
      return;
    }

    const finalUrl = getShareUrlWithSettings(baseShareUrl, checked);

    // Regenerate text share message
    setShareMessage(generateShareMessage(finalUrl, textFormat));

    // Regenerate reddit markdown
    const ratesForMarkdown = checked
      ? categoryEarnRates
      : defaultCategoryEarnRates || categoryEarnRates;
    if (ratesForMarkdown) {
      const markdown = generateRedditMarkdown(ratesForMarkdown, finalUrl, selectedCards);
      setRedditMarkdown(markdown);
    }
  };

  const handleCopyShareMessage = async () => {
    try {
      await copyToClipboard(shareMessage);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // Silently fail
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        text: shareMessage,
        url: baseShareUrl,
      });
    } catch {
      // User cancelled or error
    }
  };

  const handleCopyRedditMarkdown = async () => {
    try {
      await copyToClipboard(redditMarkdown);
      setRedditCopied(true);
      setTimeout(() => setRedditCopied(false), 2000);
    } catch {
      // Silently fail
    }
  };

  const handleCopyUrl = async () => {
    try {
      const finalUrl = getShareUrlWithSettings(baseShareUrl, includeSettings);
      await copyToClipboard(finalUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      // Silently fail
    }
  };

  const currentCopied =
    shareTab === 'url' ? urlCopied : shareTab === 'text' ? shareCopied : redditCopied;

  const linkElement = savedUrl ? (
    <Tooltip content={copied ? 'Copied!' : 'Copy'} forceShow={copied}>
      <a
        href={savedUrl}
        onClick={(e) => {
          e.preventDefault();
          copyToClipboard(savedUrl);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: '#4DA9E6',
          wordBreak: 'break-all',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'baseline',
          gap: 4,
          textDecoration: 'none',
        }}
      >
        {savedUrl}
        <FontAwesomeIcon
          icon={copied ? faCheck : faCopy}
          style={{
            fontSize: 11,
            flexShrink: 0,
            color: copied ? '#10b981' : undefined,
          }}
        />
      </a>
    </Tooltip>
  ) : null;

  const linkSkeleton = !savedUrl ? (
    <div
      style={{
        width: 140,
        height: 18,
        borderRadius: 999,
        backgroundColor: '#e5e7eb',
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  ) : null;

  const shareButton = (
    <button
      onClick={() => handleOpenShareModal('text')}
      disabled={isSavingLink}
      style={{
        padding: '8px 16px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: 'white',
        fontWeight: 500,
        fontSize: 14,
        cursor: isSavingLink ? 'progress' : 'pointer',
        transition: 'background-color 0.2s ease',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        opacity: isSavingLink ? 0.8 : 1,
      }}
    >
      <FontAwesomeIcon icon={faShareNodes} />
      {isSavingLink ? 'Generating…' : 'Share'}
    </button>
  );

  const redditButton =
    categoryEarnRates && Object.keys(categoryEarnRates).length > 0 ? (
      <button
        onClick={() => handleOpenShareModal('reddit')}
        disabled={isSavingLink}
        style={{
          padding: '8px 16px',
          borderRadius: '8px',
          border: 'none',
          backgroundColor: '#FF4500',
          color: 'white',
          fontWeight: 500,
          fontSize: 14,
          cursor: isSavingLink ? 'progress' : 'pointer',
          transition: 'background-color 0.2s ease',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          opacity: isSavingLink ? 0.8 : 1,
        }}
      >
        <FontAwesomeIcon icon={faReddit} style={{ fontSize: 16 }} />
      </button>
    ) : null;

  const removeAllButton = onRemoveAll ? (
    <button
      onClick={() => setShowRemoveAllModal(true)}
      style={{
        padding: '8px 16px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#FEE',
        color: '#CC0000',
        fontWeight: 400,
        fontSize: 14,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#FDD';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#FEE';
      }}
    >
      Remove all
    </button>
  ) : null;

  const removeAllModal = onRemoveAll ? (
    <Modal
      isOpen={showRemoveAllModal}
      handleClose={() => {
        setShowRemoveAllModal(false);
      }}
      cancelText="Cancel"
      extraFooterButtons={
        <ActionButton
          variant="danger"
          onClick={() => {
            onRemoveAll();
            setShowRemoveAllModal(false);
          }}
        >
          Remove All
        </ActionButton>
      }
    >
      <h2>Remove all cards?</h2>
      <p>
        This will remove all cards from your wallet. You can add them back at
        any time.
      </p>
    </Modal>
  ) : null;

  const shareModal = (
    <Modal
      isOpen={showShareModal}
      handleClose={() => {
        setShowShareModal(false);
        setShareCopied(false);
        setRedditCopied(false);
        setUrlCopied(false);
      }}
      style={{ maxWidth: 600 }}
    >
      <h2 style={{ margin: '0 0 12px 0', fontSize: 24 }}>Share Wallet</h2>

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
          fontSize: 13,
          color: '#374151',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={includeSettings}
          onChange={(e) => handleIncludeSettingsChange(e.target.checked)}
          style={{ cursor: 'pointer' }}
        />
        Include point values and settings in links
      </label>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <SegmentedControl
          options={[
            { value: 'url', label: 'URL' },
            { value: 'text', label: 'Text' },
            { value: 'reddit', label: 'Reddit' },
          ]}
          value={shareTab}
          onChange={(v) => setShareTab(v as 'url' | 'text' | 'reddit')}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          {shareTab === 'text' && !isDesktop && !!navigator.share && (
            <button
              onClick={handleNativeShare}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#6366f1',
                color: 'white',
                fontWeight: 500,
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <FontAwesomeIcon icon={faShareNodes} />
              Share
            </button>
          )}
          <button
            onClick={
              shareTab === 'url'
                ? handleCopyUrl
                : shareTab === 'text'
                  ? handleCopyShareMessage
                  : handleCopyRedditMarkdown
            }
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor:
                currentCopied
                  ? '#10b981'
                  : '#e5e7eb',
              color:
                currentCopied
                  ? 'white'
                  : '#374151',
              fontWeight: 500,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
            }}
          >
            <FontAwesomeIcon
              icon={currentCopied ? faCheck : faCopy}
            />
            {currentCopied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {shareTab === 'text' && (
        <div style={{ marginBottom: 8 }}>
          <SegmentedControl
            title="Format:"
            options={[
              { value: 'compact', label: 'Compact' },
              { value: 'full', label: 'Full' },
            ]}
            value={textFormat}
            onChange={(v) => handleTextFormatChange(v as 'compact' | 'full')}
          />
          <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>
            {textFormat === 'compact' ? 'Top 5 categories' : 'All categories'}
          </span>
        </div>
      )}

      {shareTab === 'url' ? (
        <div
          style={{
            padding: 16,
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            backgroundColor: '#f9fafb',
            wordBreak: 'break-all',
            fontSize: 14,
            fontFamily: 'monospace',
            color: '#1a1a1a',
          }}
        >
          {getShareUrlWithSettings(baseShareUrl, includeSettings)}
        </div>
      ) : (
        <textarea
          readOnly
          value={shareTab === 'text' ? shareMessage : redditMarkdown}
          style={{
            width: '100%',
            height: shareTab === 'text' ? 180 : 300,
            padding: 12,
            fontSize: 13,
            fontFamily: 'monospace',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            resize: 'vertical',
            boxSizing: 'border-box',
            backgroundColor: '#f9fafb',
          }}
          onFocus={(e) => e.target.select()}
        />
      )}
      <p style={{ margin: '12px 0 0 0', fontSize: 13, color: '#6b7280' }}>
        {shareTab === 'url'
          ? 'Copy this link to share your wallet.'
          : shareTab === 'text'
            ? 'Copy and paste this text to share your wallet.'
            : 'Paste this into a Reddit comment or post to share wallet.'}
      </p>
    </Modal>
  );

  const pulseStyle = (
    <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
  );

  if (variant === 'desktop' && children) {
    return (
      <>
        {pulseStyle}
        {children({
          linkArea: (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
                minWidth: 0,
              }}
            >
              {linkElement || linkSkeleton}
            </div>
          ),
          shareButton,
          redditButton,
        })}
        {shareModal}
      </>
    );
  }

  return (
    <>
      {pulseStyle}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '14px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          {linkElement || linkSkeleton}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {shareButton}
          {redditButton}
          {removeAllButton}
        </div>
      </div>
      {removeAllModal}
      {shareModal}
    </>
  );
};
